import { ApiClient, ApiError } from './api-client';
import { takeLegacyPersistedSession, useAuthStore } from './auth-store';
import type { LoginResponse } from './types';
import {
  createDesktopFetch,
  getDesktopRuntime,
  resolveApiBaseUrl,
} from './runtime-config';

export { ApiError };

let unauthorizedHandler: () => void = () => {
  useAuthStore.getState().clear();
};

export function setUnauthorizedHandler(fn: () => void) {
  unauthorizedHandler = fn;
}

// 桌面壳（Issue #121）注入运行期桥时：base URL 取主进程下发的绝对地址，
// 网络请求经主进程 net.fetch 的 IPC 传输发出；否则保持 Web 端原行为（相对
// /api/v1 + 标准 fetch），两端共用同一 ApiClient 契约。
const desktopRuntime = getDesktopRuntime();
const baseUrl = resolveApiBaseUrl(desktopRuntime);
const fetchImpl = desktopRuntime ? createDesktopFetch(desktopRuntime) : undefined;

export const apiClient = new ApiClient({
  baseUrl,
  getToken: () => useAuthStore.getState().accessToken,
  onUnauthorized: () => unauthorizedHandler(),
  fetchImpl,
  // Issue #322 / #401：管理后台 240 分钟滑动续期——access token 过期（401）时单飞
  // 刷新并重放原请求。#401 起刷新凭 HttpOnly Cookie（不再提供 getRefreshToken），
  // refresh token 全程不进入前端 JS；成功仅把新 access token 写回内存 auth store，
  // 刷新失败才由 onUnauthorized 清会话跳登录（无无限重试）。
  onRefreshed: ({ accessToken }) =>
    useAuthStore.getState().setSession({ accessToken }),
});

let bootstrapPromise: Promise<void> | null = null;

/**
 * Issue #401：应用启动期会话引导（App 挂载时调用一次，幂等）。
 *
 *   1. 迁移兼容：清除 V1.0.6 及之前 persist 在 localStorage 的会话；其中的旧
 *      refreshToken 尽力调用 /auth/logout 向后端撤销（失败静默——本地已删除，
 *      服务端令牌最迟随 240 分钟 TTL 过期）。用户走重新登录路径。
 *   2. 凭 HttpOnly Cookie 静默续期一次：成功则恢复内存会话（刷新页面不掉登录），
 *      失败（无 Cookie / 已过期）保持未登录，由路由守卫引导到 /login。
 *   3. 无论结果如何都置 sessionReady，路由守卫在此之前渲染「验证中」。
 */
export function bootstrapSession(): Promise<void> {
  if (!bootstrapPromise) bootstrapPromise = doBootstrap();
  return bootstrapPromise;
}

async function doBootstrap(): Promise<void> {
  try {
    await revokeLegacySession();
    const session = await apiClient.post<Pick<LoginResponse, 'accessToken'>>(
      '/auth/refresh',
    );
    if (session?.accessToken) {
      useAuthStore.getState().setSession({ accessToken: session.accessToken });
    }
  } catch {
    // 无有效 Cookie 会话（首次访问 / 已登出 / 已过期）：保持未登录即可。
  } finally {
    useAuthStore.getState().setSessionReady();
  }
}

/** 尽力撤销旧 localStorage 会话的 refreshToken（需要旧 accessToken 作 Bearer）。 */
async function revokeLegacySession(): Promise<void> {
  const legacy = takeLegacyPersistedSession();
  if (!legacy?.refreshToken || !legacy.accessToken) return;
  const doFetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  try {
    await doFetch(`${baseUrl}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${legacy.accessToken}`,
      },
      body: JSON.stringify({ refreshToken: legacy.refreshToken }),
    });
  } catch {
    // 旧 accessToken 已过期 / 网络失败：静默放弃，服务端令牌随 TTL 过期。
  }
}

/**
 * Issue #401：管理端登出——调用真实后端 /auth/logout（撤销 Cookie 中的 refresh
 * token 并清除 Cookie），随后清内存会话。后端失败（网络/401）不阻塞本地登出。
 */
export async function logoutSession(): Promise<void> {
  try {
    await apiClient.post<void>('/auth/logout');
  } catch {
    // 即使后端不可达也完成本地登出；Cookie 令牌最迟随 TTL 过期。
  } finally {
    useAuthStore.getState().clear();
  }
}
